// Local timings, pure parts (U0): when a visit begins, when a heartbeat is due, and card-switch
// latency (j/k pressed → the next card painted). Kept tiny and off the hot path: a key press only
// stores a number; samples are batched and posted later (see useMeasure.ts).
import type { TimingSample } from "@shared/measure";

/** Coming back after this long away (hidden tab or no input) starts a new visit. */
export const RETURN_AFTER_MS = 15 * 60_000;
/** Post samples once this many are waiting, or this long after the first one. */
export const FLUSH_AT = 20;
export const FLUSH_AFTER_MS = 10_000;
/** A "switch" that takes longer than this was something else (a dialog, a slow network). */
export const MAX_SWITCH_MS = 2_000;

export const minuteOf = (epochMs: number) => Math.floor(epochMs / 60_000);

/** True when input or a visible tab after `lastSeen` means the owner came back for a new visit. */
export function isReturn(lastSeen: number | null, now: number): boolean {
  return lastSeen !== null && now - lastSeen >= RETURN_AFTER_MS;
}

/** One heartbeat per minute with input, at most. */
export function heartbeatDue(lastBeatMinute: number | null, now: number): boolean {
  return minuteOf(now) !== lastBeatMinute;
}

export class SampleBuffer {
  private items: TimingSample[] = [];

  /** Adds a sample; true when the batch is full and should be posted now. */
  push(sample: TimingSample): boolean {
    this.items.push(sample);
    return this.items.length >= FLUSH_AT;
  }

  get size(): number {
    return this.items.length;
  }

  drain(): TimingSample[] {
    const out = this.items;
    this.items = [];
    return out;
  }
}

/**
 * The pending card switch: started on j/k (from the card on screen), finished when a different
 * card paints. A press that moved nothing (the end of the list) never pairs with a later click.
 */
export class SwitchTimer {
  private pending: { at: number; from: string | null } | null = null;

  start(now: number, fromKey: string | null): void {
    this.pending = { at: now, from: fromKey };
  }

  /** The switch's latency, or null when none was pending, nothing moved, or it took too long. */
  finish(now: number, key: string): number | null {
    const p = this.pending;
    if (!p || key === p.from) return null;
    this.pending = null;
    const ms = now - p.at;
    return ms >= 0 && ms <= MAX_SWITCH_MS ? ms : null;
  }
}

export function visitId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
